/**
 * Material-UI styles and styled components for the TakeSimDetails component.
 *
 * @module components/sim/takeSim/TakeSimDetails
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import {
    makeStyles, withStyles, Tabs, Avatar,
} from '@material-ui/core';

/**
 * A hook for generating styles used in the tab content component.
 *
 * This hook utilizes Material-UI's `makeStyles` function to define
 * a set of reusable CSS styles for customizing the appearance of
 * various elements within a tab content layout.
 *
 * Defined styles:
 * - `root`: Styles for the root container, enabling flexible growth of the content.
 * - `cancelButton`: Custom color styles for the cancel button.
 * - `arrowDropDown`: Background color, margin, and border radius for dropdown arrows.
 * - `title`: Background color, padding, border radius, and margins for titles.
 * - `editIcon`: Icon size, color, and cursor styling for editable icons.
 * - `grid`: Padding adjustment for grids in the layout.
 * - `selectedCheckbox`: Styling for the selected checkbox, including custom colors.
 * - `checkbox`: General styling for checkboxes with inherited padding.
 * - `span`: Font size, weight, and padding for `span` elements.
 * - `textFont`: Font size style applied to text elements.
 * - `anim`: Basic font size and commented-out keyframe animations for text input effects.
 * - `gridItem`: Padding adjustments for grid items, with important flag for overrides.
 * - `linkTag`: Dotted border and padding for elements styled as links.
 * - `@keyframes typewriter`: Keyframes for a typewriter effect animation on text width.
 * - `@keyframes blinkTextCursor`: Keyframes for a blinking text cursor effect.
 * - `select`: Styles for native select elements, including font size, color, and icon alignment.
 */
export const useTabContentStyles = makeStyles((/* theme */) => ({
    root: {
        flexGrow: 1,
    },
    cancelButton: {
        color: '#313387',
    },
    arrowDropDown: {
        backgroundColor: '#D7D7D7',
        margin: '3px 5px -5px 5px',
        borderRadius: '3px',
    },
    title: {
        backgroundColor: 'rgb(239,239,239)',
        padding: 2,
        borderRadius: 3,
        margin: 5,
    },
    editIcon: {
        color: '#5c77778c',
        fontSize: 20,
        cursor: 'pointer',
    },
    grid: {
        paddingTop: 8,
    },
    selectedCheckbox: {
        padding: 'inherit',
        color: 'rgb(26,77,158) !important',
    },
    checkbox: {
        padding: 'inherit',
    },
    span: {
        padding: 8,
        fontWeight: 600,
        fontSize: 14,
    },
    textFont: {
        fontSize: 13,
    },
    anim: {
        fontSize: 13,
        // '& .MuiInputBase-input': {
        //   animation: '$typewriter 4s steps(44) 1s 1 normal both, $blinkTextCursor 500ms steps(44) infinite normal',
        // },
    },
    gridItem: {
        padding: '5px 25px !important',
    },
    linkTag: {
        borderBottom: '1px dotted #949494',
        padding: 3,
    },
    '@keyframes typewriter': {
        from: {
            width: 0,
        },
        to: {
            width: '100%',
        },
    },
    '@keyframes blinkTextCursor': {
        from: {
            'border-right-color': 'black',
        },
        to: {
            'border-right-color': 'transparent',
        },
    },
    select: {
        '& .MuiNativeSelect-select': {
            fontSize: 12,
            color: '#646466',
            marginLeft: 20,
        },
        '& .MuiNativeSelect-icon': {
            left: '-6px',
        },
    },
}));

/**
 * Custom hook for generating styles specific to the Navbar component.
 *
 * The `useNavbarStyles` hook utilizes the `makeStyles` utility to define and manage
 * CSS-in-JS styles. The styles created by this hook are scoped to components
 * referencing this hook, ensuring predictable and modular styling.
 *
 * Defined styles:
 * - `navbar`: Styles applied to the Navbar container, including background color and width.
 * - `cancelButton`: Styles applied to the cancel button within the Navbar, specifying the text color.
 *
 * This hook should be used within components that require consistent and reusable
 * Navbar and button styling.
 */
export const useNavbarStyles = makeStyles(() => ({
    navbar: {
        backgroundColor: '#ffffff',
        width: '100%',
    },
    cancelButton: {
        color: '#313387',
    },
}));

/**
 * A collection of styles created using Material-UI's `makeStyles` utility that defines various styling properties used across components.
 *
 * Properties:
 *
 * - `padding`: Adds padding using the spacing configuration from the theme.
 * - `cancelButton`: Styles a cancel button with a custom text color.
 * - `demo1`: Specifies a background color.
 * - `background`: Defines background color and customized padding for a section.
 * - `bgContent`: Specifies a background color and custom padding for content areas.
 * - `root`: Configures a flex container with a grow factor of 1.
 * - `paper`: Styles Material-UI `Paper` components with custom padding and text color.
 * - `topHeaderFont`: Defines styles for a top header text, including color and font size.
 * - `bottomHeaderFont`: Styles a bottom header text with specific color and font size.
 * - `height`: Sets the minimum height and background color for a content section.
 * - `tabPadding`: Adds padding for tab content using explicit overrides.
 * - `tabHeader`: Defines background color, height, and min-height for tab headers.
 * - `tabs`: Styles header tabs with a specific text color.
 * - `tabBody`: Configures a tab body background and removes padding.
 * - `txtAlign`: Aligns text to the right and sets font size.
 * - `history`: Styles history text with font size and line height.
 * - `historyPadding`: Adds padding for history sections.
 * - `button`: Styles buttons with specific size, color, font, and background settings.
 * - `icon`: Defines styles for an icon, including dimensions, background, border radius, and margin.
 * - `center`: Centers content and defines a vertical margin.
 * - `tabFont`: Sets font size for text in tabs.
 * - `moreButton`: Styles "load more" buttons with background, font size, padding, text alignment, and cursor.
 * - `buttons`: Consistent button styling with padding, font size, and text alignment.
 * - `select`: Styles dropdown components with width, padding, height, and background.
 * - `margin`: Adds vertical margins and a specific background color.
 * - `formFont`: Configures font size for form inputs or labels.
 * - `title`: Defines styles for titles with font size, weight, color, and flex behavior.
 * - `selectedButton`: Styles selected buttons with custom background, font size, text alignment, and border radius.
 * - `textFieldColor`: Configures the text color of text fields.
 * - `img`: Defines dimensions for small icons or images.
 * - `case`: Configures styles for case labels with font weight, size, and color.
 * - `border`: Adds a bottom border and vertical padding.
 * - `moreTitle`: Styles titles within expandable "more" sections.
 * - `keywordSelectionGrid`: Sets max height and overflow for scrolling.
 * - `download`: Adjusts dimensions, color, and margin for download indicators.
 * - `listAction`: Defines right alignment for list action items.
 * - `toggleBtn`: Styles toggle buttons with borders, spacing, and interaction states.
 * - `tab`: Configures the padding, size, and text properties of individual tabs.
 * - `gridTab`: Adds padding for grid-based tab layouts.
 * - `footerButton`: Styles sticky footer buttons with padding, font size, background color, and alignment.
 * - `fBtn`: Adds margin around footer buttons.
 */
export const useBasicInfoStyles = makeStyles((theme) => ({
    padding: {
        padding: theme.spacing(3),
    },
    cancelButton: {
        color: '#313387',
    },
    demo1: {
        backgroundColor: 'rgb(223,230,242)',
    },
    background: {
        backgroundColor: 'rgb(38,65,114)',
        padding: '0px 5px 0px 12px !important',
    },
    bgContent: {
        backgroundColor: 'rgb(28,52,95)',
        padding: '10px 12px 10px 25px !important',
    },
    root: {
        flexGrow: 1,
    },
    paper: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
    topHeaderFont: {
        color: '#ffffffb3',
        fontSize: 11,
    },
    bottomHeaderFont: {
        color: '#FFFFFF',
        fontSize: 13,
    },
    height: {
        minHeight: '40rem',
        backgroundColor: '#FFFFFF',
    },
    tabPadding: {
        padding: '25px !important',
    },
    tabHeader: {
        backgroundColor: '#30565526',
        height: 40,
        minHeight: '0px !important',
    },
    tabs: {
        color: 'rgb(5, 37, 76)',
    },
    tabBody: {
        backgroundColor: '#e2eeef54',
        padding: 0,
    },
    txtAlign: {
        textAlign: 'right',
        fontSize: 12,
    },
    history: {
        fontSize: 12,
        lineHeight: 2,
    },
    historyPadding: {
        padding: 15,
    },
    button: {
        color: '#1d3cd4',
        fontSize: 12,
        width: 130,
        border: 'none',
        backgroundColor: '#bfd3d882',
    },
    icon: {
        padding: '0px',
        width: 30,
        height: 30,
        backgroundColor: 'rgb(70,93,135)',
        borderRadius: '11%',
        margin: '0px 6px',
    },
    center: {
        textAlign: 'center',
        marginTop: 10,
    },
    tabFont: {
        fontSize: 13,
    },
    moreButton: {
        background: '#9caabb87',
        padding: '5px !important',
        fontSize: 11,
        textAlign: 'center',
        margin: 5,
        borderRadius: 5,
        minWidth: 42,
        cursor: 'pointer',
    },
    buttons: {
        padding: '7px !important',
        textAlign: 'center',
        fontSize: 12,
    },
    select: {
        width: '100%',
        padding: theme.spacing(0, 1, 0, 1),
        margin: '8px 0px',
        height: 40,
        background: '#FFFFFF',
    },
    margin: {
        margin: '8px 0px',
        background: '#FFFFFF',
    },
    formFont: {
        fontSize: 12,
    },
    title: {
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
        borderRadius: 5,
        flexGrow: 1,
    },
    selectedButton: {
        backgroundColor: 'rgb(86,114,163)',
        color: '#FFFFFF',
        padding: '5px !important',
        fontSize: 11,
        textAlign: 'center',
        margin: 5,
        borderRadius: 5,
        minWidth: 42,
        cursor: 'pointer',
    },
    textFieldColor: {
        color: '#FFFFFF',
    },
    img: {
        height: 15,
        width: 12,
    },
    case: {
        fontWeight: 100,
        fontSize: 12,
        color: '#ffffffe8',
    },
    border: {
        borderBottom: '1px solid #ded7d7',
        padding: '10px 0px',
    },
    moreTitle: {
        background: 'rgb(223,230,242)',
        color: 'rgb(38,65,114)',
        padding: '7px 15px !important',
        margin: 5,
        borderRadius: 4,
        fontWeight: 600,
    },
    keywordSelectionGrid: {
        maxHeight: '75vh',
        overflowY: 'scroll',
    },
    download: {
        height: 20,
        color: 'white',
        marginTop: 6,
    },
    listAction: {
        right: 5,
    },
    toggleBtn: {
        height: 32,
        border: '1px solid #465d87',
        margin: '0px 6px',
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: '#465d87',
            color: '#fff',
            fontSize: 12,
        },
        '& .MuiToggleButton-root': {
            color: '#fff',
            fontSize: 12,
        },
    },
    tab: {
        padding: theme.spacing(0),
        minWidth: '125px',
        width: '120px',
        fontSize: '11px',
        minHeight: '30px',
        height: '30px',
        textTransform: 'none',
    },
    gridTab: {
        padding: 20,
    },
    footerButton: {
        position: 'sticky',
        bottom: 0,
        padding: 10,
        fontSize: 20,
        display: 'inline-flex',
        width: 'inherit',
        background: '#efefef',
        justifyContent: 'center',
    },
    fBtn: {
        margin: 5,
    },
}));

/**
 * AntTabs is a customized version of the Material-UI `Tabs` component.
 * It applies additional styles to modify the appearance and behavior of tabs in the user interface.
 *
 * The `root` style adds a bottom border and adjusts the minimum height. It also customizes
 * the overflow behavior for fixed tabs to allow scrolling when necessary.
 *
 * The `indicator` style changes the default tab indicator color to a specific shade of blue.
 *
 * This component is styled using the `withStyles` higher-order component to apply the customizations.
 */
export const AntTabs = withStyles({
    root: {
        borderBottom: '1px solid #e8e8e8',
        minHeight: '2px',
        '& .MuiTabs-fixed': {
            overflow: 'auto !important',
        },
    },
    indicator: {
        backgroundColor: 'rgb(70,117,211)',
    },
})(Tabs);

/**
 * A styled avatar component with smaller dimensions and additional margin.
 *
 * This component is created using the `withStyles` higher-order function to apply
 * custom styles to the `Avatar` component. The applied styles adjust the width,
 * height, and bottom margin of the avatar for use in compact layouts.
 *
 * Styling:
 * - `marginBottom`: Defines the bottom margin of the avatar (5 pixels).
 * - `width`: Sets the width of the avatar (18 pixels).
 * - `height`: Sets the height of the avatar (18 pixels).
 *
 * Usage of this component is suited for scenarios requiring a smaller avatar,
 * such as inside lists or tightly spaced UI components.
 */
export const SmallAvatar = withStyles(() => ({
    root: {
        marginBottom: 5,
        width: 18,
        height: 18,
    },
}))(Avatar);
